import React, { useEffect, useState } from 'react';
import { C, Card, Btn, Input } from '../components/UI';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { MilestonePaymentForm } from '../components/PaymentForm';
import { useBranding } from '../context/BrandingContext';
import { api, PreorderOfferingResponse } from '../services/api';

// Public, guest-checkout page — no login required. Reachable at /preorder. Lists whatever
// PricingRule rows admin has marked PreorderEnabled (see AdminDashboard's PricingPanel);
// for a single offering the form is shown directly rather than making the visitor pick
// from a list of one.
//
// Embeddable via iframe (e.g. on www.box-runner.net, a separate site from this app —
// see staticwebapp.config.json's per-route CSP frame-ancestors override for /preorder).
// ?embed=1 hides this page's own branded header, since an embedding site already has one.

export default function PreorderPage() {
    const branding = useBranding();
    const [offerings, setOfferings] = useState<PreorderOfferingResponse[] | null>(null);
    const [selected, setSelected] = useState<PreorderOfferingResponse | null>(null);
    const [error, setError] = useState('');
    const embed = new URLSearchParams(window.location.search).get('embed') === '1';

    useEffect(() => {
        api.getPreorderOfferings()
            .then(list => {
                setOfferings(list);
                if (list.length === 1) setSelected(list[0]);
            })
            .catch(e => setError(e.message));
    }, []);

    return (
        <div style={{ minHeight: embed ? 'auto' : '100vh', background: embed ? 'transparent' : C.bg, fontFamily: "'Inter', sans-serif", color: C.text }}>
            {!embed && (
                <div style={{ background: branding.primaryColor, padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 56 }}>
                    {branding.logoUrl
                        ? <img src={branding.logoUrl} alt={branding.appName} style={{ height: 32, maxWidth: 120, objectFit: 'contain' }} />
                        : <span style={{ fontSize: 18, fontWeight: 700, color: branding.navTextColor ?? '#fff', letterSpacing: -0.5 }}>{branding.appName}</span>
                    }
                    <span style={{ color: branding.navTextColor ?? '#fff', fontSize: 13, opacity: 0.7 }}>· Preorder</span>
                </div>
            )}

            <div style={{ maxWidth: 480, margin: '0 auto', padding: embed ? '0' : '32px 20px' }}>
                {error && <Card style={{ marginBottom: 16 }}><div style={{ color: '#DC2626', fontSize: 13 }}>{error}</div></Card>}

                {offerings === null && !error && (
                    <div style={{ textAlign: 'center', color: C.textSub, padding: 40 }}>Loading…</div>
                )}

                {offerings !== null && offerings.length === 0 && (
                    <Card style={{ textAlign: 'center', padding: 40 }}>
                        <div style={{ fontSize: 32, marginBottom: 8 }}>📦</div>
                        <div style={{ fontWeight: 600, marginBottom: 4 }}>No preorders available right now</div>
                        <div style={{ fontSize: 13, color: C.textSub }}>Check back soon.</div>
                    </Card>
                )}

                {offerings !== null && offerings.length > 1 && !selected && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {offerings.map(o => (
                            <Card key={o.pricingRuleId} style={{ cursor: 'pointer' }}>
                                <div onClick={() => setSelected(o)} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div>
                                        <div style={{ fontWeight: 700 }}>{o.serviceTypeName}</div>
                                        {o.description && <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>{o.description}</div>}
                                    </div>
                                    <div style={{ fontWeight: 700, color: C.greenMid ?? '#1D9E75' }}>${o.price.toFixed(2)}</div>
                                </div>
                            </Card>
                        ))}
                    </div>
                )}

                {selected && <PreorderCheckout offering={selected} onBack={offerings && offerings.length > 1 ? () => setSelected(null) : undefined} />}
            </div>
        </div>
    );
}

function PreorderCheckout({ offering, onBack }: { offering: PreorderOfferingResponse; onBack?: () => void }) {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [address, setAddress] = useState('');
    const [addressDetail, setAddressDetail] = useState<{ lat: number; lng: number; city?: string; state?: string; county?: string; postalCode?: string; country?: string } | null>(null);
    const [quantity, setQuantity] = useState(1);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [coverage, setCoverage] = useState<'checking' | 'covered' | 'uncovered' | null>(null);

    useEffect(() => {
        if (!addressDetail) { setCoverage(null); return; }
        let cancelled = false;
        setCoverage('checking');
        api.checkServiceArea(addressDetail.state, addressDetail.county, addressDetail.postalCode)
            .then(r => { if (!cancelled) setCoverage(r.covered ? 'covered' : 'uncovered'); })
            .catch(() => { if (!cancelled) setCoverage(null); });
        return () => { cancelled = true; };
    }, [addressDetail]);

    const [checkout, setCheckout] = useState<{ preorderId: string; clientSecret: string; paymentIntentId: string } | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [done, setDone] = useState(false);

    const total = offering.price * quantity;

    const submit = async () => {
        if (!name.trim() || !email.trim() || !addressDetail) {
            setError('Please fill in your name, email, and select a delivery address from the dropdown.');
            return;
        }
        if (coverage === 'uncovered') {
            setError("Sorry, we don't currently deliver to this address.");
            return;
        }
        setSubmitting(true); setError('');
        try {
            const res = await api.createProductPreorder({
                pricingRuleId: offering.pricingRuleId,
                contactName: name, contactEmail: email, contactPhone: phone || undefined,
                deliveryAddress: address, deliveryLat: addressDetail.lat, deliveryLng: addressDetail.lng,
                city: addressDetail.city, state: addressDetail.state, county: addressDetail.county, postalCode: addressDetail.postalCode, country: addressDetail.country,
                quantity,
            });
            setCheckout({ preorderId: res.preorder.id, clientSecret: res.clientSecret, paymentIntentId: res.paymentIntentId });
        } catch (e: any) { setError(e.message); }
        finally { setSubmitting(false); }
    };

    const onPaymentSuccess = async () => {
        if (!checkout) return;
        setConfirming(true); setError('');
        try {
            await api.confirmProductPreorder(checkout.preorderId, checkout.paymentIntentId);
            setDone(true);
        } catch (e: any) { setError(e.message); }
        finally { setConfirming(false); }
    };

    if (done) {
        return (
            <Card style={{ textAlign: 'center', padding: 40 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>✅</div>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 6 }}>Preorder confirmed!</div>
                <div style={{ fontSize: 13, color: C.textSub }}>A confirmation email is on its way to {email}. We'll be in touch once it's ready for delivery.</div>
            </Card>
        );
    }

    if (checkout) {
        return (
            <div>
                {error && <Card style={{ marginBottom: 16 }}><div style={{ color: '#DC2626', fontSize: 13 }}>{error}</div></Card>}
                <MilestonePaymentForm
                    clientSecret={checkout.clientSecret}
                    label={offering.serviceTypeName}
                    amount={total}
                    onSuccess={onPaymentSuccess}
                    onCancel={() => setCheckout(null)}
                />
                {confirming && <div style={{ textAlign: 'center', fontSize: 12, color: C.textSub, marginTop: 12 }}>Confirming…</div>}
            </div>
        );
    }

    return (
        <Card>
            <h2 style={{ margin: '0 0 4px', fontSize: 18 }}>Preorder {offering.serviceTypeName}</h2>
            {offering.description && <p style={{ color: C.textSub, fontSize: 13, margin: '0 0 16px' }}>{offering.description}</p>}
            {error && <div style={{ color: '#DC2626', fontSize: 13, marginBottom: 12 }}>{error}</div>}

            <Input label="Full name" value={name} onChange={setName} placeholder="Jane Smith" required />
            <Input label="Email" value={email} onChange={setEmail} type="email" placeholder="jane@example.com" required />
            <Input label="Phone (optional)" value={phone} onChange={setPhone} type="tel" placeholder="(555) 555-1234" />
            <AddressAutocomplete
                label="Delivery address"
                value={address}
                onChange={r => { setAddress(r.address); setAddressDetail({ lat: r.lat, lng: r.lng, city: r.city, state: r.state, county: r.county, postalCode: r.postalCode, country: r.country }); }}
                required
            />
            {coverage === 'checking' && (
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: -8, marginBottom: 14 }}>Checking delivery area…</div>
            )}
            {coverage === 'uncovered' && (
                <div style={{ fontSize: 12, color: '#DC2626', marginTop: -8, marginBottom: 14 }}>Sorry, we don't currently deliver to this address.</div>
            )}

            <div style={{ marginBottom: 16 }}>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 500, color: C.textSub, marginBottom: 5 }}>Quantity</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <button type="button" onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', fontSize: 16, cursor: 'pointer' }}>−</button>
                    <span style={{ fontWeight: 700, minWidth: 20, textAlign: 'center' }}>{quantity}</span>
                    <button type="button" onClick={() => setQuantity(q => Math.min(20, q + 1))}
                        style={{ width: 32, height: 32, borderRadius: 8, border: `1px solid ${C.border}`, background: '#fff', fontSize: 16, cursor: 'pointer' }}>+</button>
                </div>
            </div>

            <div style={{ background: C.bg, borderRadius: 8, padding: 12, marginBottom: 16, display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                <span>Total</span>
                <span>${total.toFixed(2)}</span>
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
                {onBack && <Btn variant="outline" onClick={onBack}>Back</Btn>}
                <Btn onClick={submit} disabled={submitting || coverage === 'uncovered' || coverage === 'checking'} fullWidth size="lg">
                    {submitting ? 'Loading…' : 'Continue to payment →'}
                </Btn>
            </div>
        </Card>
    );
}
